import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  ClipboardList,
  ExternalLink,
  FileText,
  Loader2,
  Package,
  Pencil,
  Pill,
  Plus,
  Save,
  Send,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  ALERT_SCORTA_LABELS,
  STATO_RICHIESTA_LABELS,
  farmDelete,
  farmGet,
  farmPost,
  farmPut,
} from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

const emptyTerapia = {
  nome: "", dosaggio: "", note: "", data_inizio: new Date().toISOString().slice(0, 10),
  data_fine: "", giorni_settimana: [], orari: ["08:00"], principio_attivo: "",
};

function fmtDate(value) {
  if (!value) return "—";
  try { return new Date(value).toLocaleDateString("it-IT"); } catch { return value; }
}

function StockAlerts({ alerts = [] }) {
  if (!alerts.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {alerts.map((alert) => {
        const meta = ALERT_SCORTA_LABELS[alert] || {
          label: alert,
          className: "border-slate-200 bg-slate-50 text-slate-600",
        };
        return (
          <Badge key={alert} variant="outline" className={`text-[10px] ${meta.className}`}>
            {meta.label}
          </Badge>
        );
      })}
    </div>
  );
}

export default function FarmaciaSchedaPaziente() {
  const { id } = useParams();
  const location = useLocation();
  const { token } = useFarmaciaAuth();
  const tipo = new URLSearchParams(location.search).get("tipo") === "cliente" ? "cliente" : "ospite";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyTerapia);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      setData(await farmGet(`/farmacia/pazienti/${tipo}/${encodeURIComponent(id)}`, token));
    } catch (error) {
      setData(null);
      toast.error(error?.response?.data?.detail || "Scheda paziente non disponibile");
    } finally {
      setLoading(false);
    }
  }, [token, id, tipo]);

  useEffect(() => { load(); }, [load]);

  const edit = (terapia) => {
    setEditingId(terapia.id);
    setForm({
      ...emptyTerapia,
      ...terapia,
      nome: terapia.farmaco || terapia.nome || "",
      note: terapia.note || (tipo === "cliente" ? terapia.frequenza || "" : ""),
      data_inizio: terapia.data_inizio || new Date().toISOString().slice(0, 10),
      giorni_settimana: terapia.giorni_settimana || [],
      orari: terapia.orari?.length ? terapia.orari : ["08:00"],
    });
  };

  const saveTerapia = async () => {
    if (!form.nome.trim() || (tipo === "ospite" && (!form.data_inizio || !form.orari?.[0]))) {
      toast.error(
        tipo === "ospite"
          ? "Farmaco, data di inizio e almeno un orario sono obbligatori"
          : "Inserisci almeno il farmaco"
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        nome: form.nome.trim(),
        data_fine: form.data_fine || null,
        orari: form.orari.filter(Boolean),
      };
      if (tipo === "cliente") {
        const row = {
          id: editingId || crypto.randomUUID?.() || String(Date.now()),
          farmaco: payload.nome,
          dosaggio: payload.dosaggio || "",
          frequenza: payload.note || "",
          note: payload.note || "",
          attiva: true,
        };
        const terapieAggiornate = editingId
          ? (data.terapie || []).map((terapia) =>
              terapia.id === editingId ? { ...terapia, ...row } : terapia
            )
          : [...(data.terapie || []), row];
        await farmPut(`/farmacia/clienti/${encodeURIComponent(id)}/terapie`, token, {
          terapie: terapieAggiornate,
        });
      } else if (editingId) {
        await farmPut(`/farmacia/ospiti/${encodeURIComponent(id)}/terapie/${encodeURIComponent(editingId)}`, token, payload);
      } else {
        await farmPost(`/farmacia/ospiti/${encodeURIComponent(id)}/terapie`, token, payload);
      }
      toast.success(editingId ? "Terapia aggiornata" : "Terapia aggiunta");
      setEditingId(null);
      setForm(emptyTerapia);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Salvataggio terapia fallito");
    } finally {
      setSaving(false);
    }
  };

  const suspend = async (terapia) => {
    if (!window.confirm(`Sospendere ${terapia.farmaco || terapia.nome}?`)) return;
    setSaving(true);
    try {
      if (tipo === "cliente") {
        await farmPut(`/farmacia/clienti/${encodeURIComponent(id)}/terapie`, token, {
          terapie: (data.terapie || []).map((item) =>
            item.id === terapia.id ? { ...item, attiva: false } : item
          ),
        });
      } else {
        await farmDelete(`/farmacia/ospiti/${encodeURIComponent(id)}/terapie/${encodeURIComponent(terapia.id)}`, token);
      }
      toast.success("Terapia sospesa");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossibile sospendere la terapia");
    } finally {
      setSaving(false);
    }
  };

  const createClientRequest = async (file) => {
    setSaving(true);
    try {
      const formData = new FormData();
      if (file) formData.append("file", file);
      await farmPost(`/farmacia/clienti/${encodeURIComponent(id)}/richieste`, token, formData, true);
      toast.success(file ? "Richiesta con PDF creata" : "Richiesta generata dalla terapia corrente");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Creazione richiesta fallita");
    } finally {
      setSaving(false);
    }
  };

  const uploadClientRicetta = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("cliente_id", id);
      const ultimaRichiesta = data?.pratiche?.[0]?.id;
      if (ultimaRichiesta) formData.append("richiesta_id", ultimaRichiesta);
      await farmPost("/farmacia/ricette/ricette-medico", token, formData, true);
      toast.success("Ricetta caricata");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Caricamento ricetta fallito");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;
  if (!data) return <div className="py-16 text-center text-sm text-slate-500">Paziente non trovato.</div>;

  const ana = data.anagrafica || {};
  const nome = `${ana.cognome || ""} ${ana.nome || ""}`.trim() || "Paziente";
  const terapie = data.terapie || [];

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <FarmaciaPageChrome
        title={nome}
        subtitle={`${tipo === "cliente" ? "Cliente diretto" : "Ospite in struttura"} · ${ana.codice_fiscale || "CF non indicato"}`}
        backTo={tipo === "cliente" ? "/farmacia/clienti" : "/farmacia/ospiti"}
        backLabel={tipo === "cliente" ? "Clienti diretti" : "Assistiti"}
      >
        {data.presa_visione?.da_visionare ? (
          <Button size="sm" onClick={async () => {
            try {
              await farmPost(`/farmacia/ospiti/${encodeURIComponent(id)}/terapia/presa-visione`, token);
              setData((prev) => ({ ...prev, presa_visione: { ...prev.presa_visione, da_visionare: false, stato: "visionata" } }));
              toast.success("Terapia visionata");
            } catch (error) { toast.error(error?.response?.data?.detail || "Conferma non riuscita"); }
          }} className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">Conferma presa visione</Button>
        ) : null}
      </FarmaciaPageChrome>

      <div className="grid gap-4 lg:grid-cols-3">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-800">Anagrafica e contatti</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <div><dt className="text-xs text-slate-400">Data di nascita</dt><dd>{ana.data_nascita || "—"}</dd></div>
            <div><dt className="text-xs text-slate-400">Telefono</dt><dd>{ana.telefono || "—"}</dd></div>
            <div><dt className="text-xs text-slate-400">Email</dt><dd>{ana.email || "—"}</dd></div>
            <div><dt className="text-xs text-slate-400">Medico curante</dt><dd>{data.medico_curante || "—"}</dd></div>
          </dl>
          {tipo === "cliente" ? <Button asChild variant="outline" size="sm" className="mt-4"><Link to="/farmacia/clienti">Modifica anagrafica</Link></Button> : null}
        </section>
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Package className="h-4 w-4 text-emerald-700" /> Scorte e copertura</h2>
            {tipo === "ospite" ? <Button asChild size="sm" variant="outline"><Link to={`/farmacia/scorte?fascicolo_id=${encodeURIComponent(id)}`}>Gestisci scorte</Link></Button> : null}
          </div>
          {tipo === "ospite" && data.ciclo_carepy ? (
            <div className={`mt-3 rounded-xl border px-3 py-2 text-sm ${
              data.ciclo_carepy.stato === "in_ritardo"
                ? "border-rose-200 bg-rose-50 text-rose-800"
                : data.ciclo_carepy.stato === "carepy_ricevuto"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-amber-200 bg-amber-50 text-amber-800"
            }`}>
              <p className="font-medium">
                {data.ciclo_carepy.stato === "in_ritardo"
                  ? "Carepy in ritardo"
                  : data.ciclo_carepy.stato === "carepy_ricevuto"
                    ? "Carepy ricevuto per il ciclo"
                    : "In attesa del prossimo Carepy"}
              </p>
              {data.ciclo_carepy.prossimo_carepy_due_at ? <p className="mt-0.5 text-xs">Atteso entro il {fmtDate(data.ciclo_carepy.prossimo_carepy_due_at)}</p> : null}
              {data.ciclo_carepy.ultima_consegna_at ? <p className="mt-0.5 text-xs">Ultima consegna: {fmtDate(data.ciclo_carepy.ultima_consegna_at)}</p> : null}
            </div>
          ) : null}
          {tipo === "cliente" ? <p className="mt-3 text-sm text-slate-400">Le rilevazioni Carepy e le scorte sono disponibili per gli ospiti delle strutture collegate.</p> : null}
          {tipo === "ospite" && !data.scorte?.length ? <p className="mt-3 text-sm text-slate-400">Nessuna rilevazione Carepy disponibile.</p> : null}
          {tipo === "ospite" && data.scorte?.length ? (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {data.scorte.map((scorta) => (
                <div key={scorta.id} className="rounded-xl bg-slate-50 p-3 text-sm">
                  <p className="font-medium text-slate-800">{scorta.farmaco}</p>
                  <p className="mt-1 text-xs text-slate-500">Disponibili: {scorta.qty_verificata ?? scorta.unita_carepy ?? scorta.unita_residue ?? "—"}</p>
                  {scorta.giorni_copertura != null ? <p className={`mt-1 text-xs font-semibold ${scorta.giorni_copertura < 7 ? "text-rose-700" : scorta.giorni_copertura < 14 ? "text-amber-700" : "text-emerald-700"}`}>Copertura stimata: {scorta.giorni_copertura} giorni</p> : null}
                  <StockAlerts alerts={scorta.alerts} />
                </div>
              ))}
            </div>
          ) : null}
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3"><h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Pill className="h-4 w-4 text-emerald-700" /> Terapia e posologia</h2>
          <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setForm(emptyTerapia); }}><Plus className="mr-1 h-3.5 w-3.5" /> Nuova terapia</Button>
        </div>
        <div className="mt-4 grid gap-2 md:grid-cols-2">
          {terapie.map((terapia) => (
            <article key={terapia.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <div className="flex justify-between gap-2"><div><p className="font-medium text-slate-800">{terapia.farmaco || terapia.nome} {terapia.dosaggio || ""}</p><p className="mt-1 text-xs text-slate-500">{(terapia.orari || []).join(", ") || terapia.frequenza || "Orario non indicato"} · dal {fmtDate(terapia.data_inizio)}</p></div>
                <Badge variant="outline" className={terapia.attiva === false ? "border-slate-200 text-slate-500" : "border-emerald-200 text-emerald-700"}>{terapia.attiva === false ? "Sospesa" : "Attiva"}</Badge></div>
              {terapia.note ? <p className="mt-2 text-xs text-slate-500">{terapia.note}</p> : null}
              <div className="mt-3 flex gap-2"><Button size="sm" variant="outline" disabled={saving} onClick={() => edit(terapia)}><Pencil className="mr-1 h-3 w-3" /> Modifica</Button>{terapia.attiva !== false ? <Button size="sm" variant="ghost" disabled={saving} className="text-rose-700" onClick={() => suspend(terapia)}><Trash2 className="mr-1 h-3 w-3" /> Sospendi</Button> : null}</div>
            </article>
          ))}
          {!terapie.length ? <p className="text-sm text-slate-400">Nessuna terapia registrata.</p> : null}
        </div>
        <div className="mt-4 grid gap-2 rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 md:grid-cols-4">
          <Input placeholder="Farmaco" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          <Input placeholder="Dosaggio" value={form.dosaggio || ""} onChange={(e) => setForm({ ...form, dosaggio: e.target.value })} />
          {tipo === "ospite" ? <Input type="date" value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} /> : <Input placeholder="Frequenza / posologia" value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} />}
          {tipo === "ospite" ? <Input type="time" value={form.orari?.[0] || ""} onChange={(e) => setForm({ ...form, orari: [e.target.value] })} /> : null}
          <Input className={tipo === "ospite" ? "md:col-span-3" : "md:col-span-2"} placeholder="Note / posologia" value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <Button disabled={saving} onClick={saveTerapia}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> {editingId ? "Aggiorna" : "Aggiungi"}</>}</Button>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><ClipboardList className="h-4 w-4" /> Richieste e ricette</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {tipo === "ospite" ? (
              <>
                <Button asChild size="sm" variant="outline"><Link to={`/farmacia/richieste?organization_id=${encodeURIComponent(data.organization_id)}&fascicolo_id=${encodeURIComponent(id)}`}><Upload className="mr-1.5 h-3.5 w-3.5" /> Carica Carepy</Link></Button>
                <Button asChild size="sm" variant="outline"><Link to={`/farmacia/pratiche?vista=copertura&organization_id=${encodeURIComponent(data.organization_id)}&q=${encodeURIComponent(ana.codice_fiscale || nome)}`}><ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Controllo ricette</Link></Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" disabled={saving} onClick={() => createClientRequest()}><Send className="mr-1.5 h-3.5 w-3.5" /> Genera richiesta</Button>
                <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"><Upload className="mr-1.5 h-3.5 w-3.5" /> Carica richiesta<input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" disabled={saving} onChange={(event) => { const file = event.target.files?.[0]; if (file) createClientRequest(file); event.target.value = ""; }} /></label>
                <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"><Upload className="mr-1.5 h-3.5 w-3.5" /> Carica ricetta<input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" disabled={saving} onChange={(event) => { const file = event.target.files?.[0]; if (file) uploadClientRicetta(file); event.target.value = ""; }} /></label>
              </>
            )}
          </div>
          <ul className="mt-4 space-y-2 text-sm">{(data.pratiche || []).slice(0, 6).map((item) => <li key={item.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2"><span>Richiesta {item.id?.slice(0, 8)}</span><Badge variant="outline">{STATO_RICHIESTA_LABELS[item.stato] || item.stato || "—"}</Badge></li>)}{!(data.pratiche || []).length ? <li className="text-slate-400">Nessuna richiesta.</li> : null}</ul>
          {(data.ricette || []).length ? <ul className="mt-2 space-y-2 text-sm">{data.ricette.slice(0, 4).map((item) => <li key={item.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2"><span>Ricetta {item.id?.slice(0, 8)}</span><span className="text-xs text-slate-500">{item.stato || "—"}</span></li>)}</ul> : null}
        </section>
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><FileText className="h-4 w-4" /> Documenti e cronologia</h2>
          <ul className="mt-3 space-y-2 text-sm">{(data.documenti || []).slice(0, 5).map((item) => <li key={item.id} className="rounded-lg bg-slate-50 px-3 py-2">{item.tipo_label || item.tipo || item.file_name}</li>)}{!(data.documenti || []).length ? <li className="text-slate-400">Nessun documento farmacia.</li> : null}</ul>
          <p className="mt-4 text-xs text-slate-500">{(data.cronologia || []).length} eventi tracciati nella cronologia.</p>
        </section>
      </div>
    </div>
  );
}
